import { useSelector } from 'react-redux';

import _get from 'lodash/get';

import { priceAdvisorText } from 'axl-config';

import globalConfigsDuck from '@/ducks/globalConfigsDuck';

/**
 * Custom hook to get PriceAdvisor configuration from Redux store
 * @returns {Object} Configuration object with API URL and key
 */
export const usePriceAdvisorConfig = () => {
    const priceAdvisorApiUrl = useSelector(globalConfigsDuck.selectors.getPriceAdvisorApiUrl);
    const priceAdvisorApiKey = useSelector(globalConfigsDuck.selectors.getPriceAdvisorApiKey);

    return {
        apiUrl: priceAdvisorApiUrl,
        clientId: priceAdvisorApiKey,
    };
};

/**
 * Determines the appropriate price advisor subheader text based on vehicle type and pricing rules
 * @param {boolean} isNewCar - Whether the vehicle is new or used
 * @param {number} msrp - Manufacturer's Suggested Retail Price
 * @param {boolean} hasStatePricingRules - Whether state-specific pricing rules apply
 * @returns {string} The appropriate subheader text for the price advisor
 */
export const getPriceAdvisorSubHeader = (isNewCar, msrp, hasStatePricingRules) => {
    const subHeader = priceAdvisorText.priceAdvisorSubHeader;

    if (!isNewCar) {
        return hasStatePricingRules ? subHeader.hasStatePricingRules.used : subHeader.used;
    }

    if (msrp > 0) {
        return hasStatePricingRules ? subHeader.hasStatePricingRules.new : subHeader.new;
    }

    return subHeader.unavailable;
};

/**
 * Gets the appropriate title text for Fair Market Range components
 * @param {boolean} isPrivateSeller - Whether this is for a private seller
 * @returns {string} The title text
 */
export const getFairMarketRangeTitle = (isPrivateSeller) => (isPrivateSeller
    ? 'Kelley Blue Book® Private Party Range'
    : 'Kelley Blue Book® Fair Market Range');

/**
 * Extracts and formats listing data for PriceAdvisor
 * @param {Object} listing - The vehicle listing object
 * @param {Object} owner - The owner/location object
 * @returns {Object} Formatted data for PriceAdvisor
 */
export const extractPriceAdvisorData = (listing, owner) => {
    const zip = owner?.location?.address?.zip ?? '';
    const { pricingDetail = {}, type = '', vin = '' } = listing;
    const { msrp } = pricingDetail;
    const priceAdvisorRuleEnabled = _get(listing, 'priceAdvisorRuleEnabled', false);

    return {
        zip,
        type,
        vin,
        msrp,
        priceAdvisorRuleEnabled,
        isNewCar: type.toLowerCase() === 'new',
    };
};

/**
 * Creates a complete PriceAdvisor props object with all common configuration
 * @param {Object} options - Configuration options
 * @param {boolean} options.isPrivateSeller - Whether this is for a private seller
 * @param {Object} options.listing - The vehicle listing object
 * @param {Object} options.owner - The owner/location object
 * @param {string} options.height - Height for the PriceAdvisor component
 * @param {boolean} options.showAskingPrice - Whether to show asking price
 * @param {Object} options.config - Price advisor configuration with apiUrl and clientId
 * @returns {Object} Complete props object for PriceAdvisor component
 */
export const createPriceAdvisorProps = ({
    isPrivateSeller,
    listing,
    owner,
    height = '308',
    showAskingPrice,
    config = {},
}) => {
    const data = extractPriceAdvisorData(listing, owner);

    return {
        advisorType: isPrivateSeller ? 'privateparty' : 'fairpurchaseprice',
        apiUrl: config.apiUrl,
        clientId: config.clientId,
        height,
        listingType: data.type,
        vin: data.vin,
        zip: data.zip,
        ...(showAskingPrice !== undefined && { showAskingPrice }),
    };
};
